import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { toast } from "sonner";

interface LocationService {
  _id: Id<"locationServices">;
  type: "cleaning" | "floorcare";
  name: string;
  frequency: string;
  description?: string;
  serviceDays?: string[];
  squareFootage?: number;
}

interface EditLocationServiceSheetProps {
  isOpen: boolean;
  onClose: () => void;
  service: LocationService | null;
  onSuccess?: () => void;
}

const SERVICE_TYPES = [
  { value: "cleaning", label: "Cleaning Service" },
  { value: "floorcare", label: "Floorcare Service" },
];

// Cleaning services (from AddCleaningServiceSheet)
const CLEANING_SERVICES = [
  "General Cleaning",
  "Post Construction Cleaning",
  "Disinfectant Cleaning",
  "Restroom Cleaning",
  "Exterior Cleaning",
  "Pressure Washing",
  "Tile & Grout Cleaning",
  "Window & Glass Cleaning",
  "Trash Chute & Dumpster Area Cleaning",
  "Commercial Kitchen Cleaning",
  "Graffiti Removal",
  "Odor Removal",
  "Upholstery & Fabric Cleaning",
  "Warehouse & Storage Area Cleaning",
  "High-Touch Surface Sanitization",
];

// Floorcare services (from AddFloorcareServiceSheet)
const FLOORCARE_SERVICES = [
  "Carpet Cleaning",
  "Floor Stripping and Waxing",
  "Tile & Grout Cleaning",
  "Concrete Polishing",
  "Hardwood Floor Refinishing",
  "Marble Restoration",
  "Stone Floor Cleaning",
  "Vinyl Floor Maintenance",
  "Terrazzo Restoration",
];

// Cleaning frequencies (from AddCleaningServiceSheet)
const CLEANING_FREQUENCIES = [
  { value: "7_per_week", label: "7 Days per Week" },
  { value: "6_per_week", label: "6 Days per Week" },
  { value: "5_per_week", label: "5 Days per Week" },
  { value: "4_per_week", label: "4 Days per Week" },
  { value: "3_per_week", label: "3 Days per Week" },
  { value: "2_per_week", label: "2 Days per Week" },
  { value: "1_per_week", label: "1 Day per Week" },
  { value: "2_per_month", label: "Bi-Monthly" },
  { value: "1_per_month", label: "Monthly" },
];

// Floorcare frequencies (from AddFloorcareServiceSheet)
const FLOORCARE_FREQUENCIES = [
  { value: "6_per_week", label: "6 Days per Week" },
  { value: "5_per_week", label: "5 Days per Week" },
  { value: "4_per_week", label: "4 Days per Week" },
  { value: "3_per_week", label: "3 Days per Week" },
  { value: "2_per_week", label: "2 Days per Week" },
  { value: "1_per_week", label: "Weekly (1 Day per Week)" },
  { value: "2_per_month", label: "Bi-Monthly" },
  { value: "1_per_month", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannually", label: "Semi-Annually" },
  { value: "annually", label: "Annually" },
];

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function EditLocationServiceSheet({
  isOpen,
  onClose,
  service,
  onSuccess,
}: EditLocationServiceSheetProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: "cleaning" as "cleaning" | "floorcare",
    name: "",
    frequency: "",
    description: "",
    serviceDays: [] as string[],
    squareFootage: "",
  });

  const updateService = useMutation(api.serviceLocations.updateLocationService);

  // Get current services and frequencies based on type
  const currentServices = formData.type === "cleaning" ? CLEANING_SERVICES : FLOORCARE_SERVICES;
  const currentFrequencies =
    formData.type === "cleaning" ? CLEANING_FREQUENCIES : FLOORCARE_FREQUENCIES;

  // Populate form when service changes
  useEffect(() => {
    if (service && isOpen) {
      setFormData({
        type: service.type,
        name: service.name,
        frequency: service.frequency,
        description: service.description || "",
        serviceDays: service.serviceDays || [],
        squareFootage: service.squareFootage?.toString() || "",
      });
    }
  }, [service, isOpen]);

  // Reset name and frequency when type changes
  const handleTypeChange = (type: "cleaning" | "floorcare") => {
    setFormData((prev) => ({
      ...prev,
      type,
      name: "", // Reset name when type changes
      frequency: "", // Reset frequency when type changes
    }));
  };

  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      serviceDays: prev.serviceDays.includes(day)
        ? prev.serviceDays.filter((d) => d !== day)
        : [...prev.serviceDays, day],
    }));
  };

  const handleSubmit = async () => {
    if (!service) return;

    if (!formData.name) {
      toast.error("Please select a service");
      return;
    }
    if (!formData.frequency) {
      toast.error("Please select a frequency");
      return;
    }

    setIsSubmitting(true);

    try {
      await updateService({
        serviceId: service._id,
        type: formData.type,
        name: formData.name,
        frequency: formData.frequency,
        description: formData.description.trim() || undefined,
        serviceDays: formData.serviceDays.length > 0 ? formData.serviceDays : undefined,
        squareFootage: formData.squareFootage ? parseFloat(formData.squareFootage) : undefined,
      });

      toast.success("Service updated successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error updating service:", error);
      toast.error("Failed to update service");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!service) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Edit Service</SheetTitle>
          <SheetDescription>Update the details of this service.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Service Type */}
          <div className="space-y-2">
            <Label>Service Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`rounded-lg border p-4 text-left transition-all ${
                    formData.type === type.value
                      ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleTypeChange(type.value as "cleaning" | "floorcare")}
                >
                  <div className="font-medium">{type.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Service Name Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="name">Service *</Label>
            <Select
              value={formData.name}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a service..." />
              </SelectTrigger>
              <SelectContent>
                {currentServices.map((svc) => (
                  <SelectItem key={svc} value={svc}>
                    {svc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Frequency Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="frequency">Frequency *</Label>
            <Select
              value={formData.frequency}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, frequency: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency..." />
              </SelectTrigger>
              <SelectContent>
                {currentFrequencies.map((freq) => (
                  <SelectItem key={freq.value} value={freq.value}>
                    {freq.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Service Days */}
          <div className="space-y-2">
            <Label>Service Days (Optional)</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day}
                  type="button"
                  className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                    formData.serviceDays.includes(day)
                      ? "border-blue-500 bg-blue-500 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                  onClick={() => handleDayToggle(day)}
                >
                  {day.substring(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Square Footage */}
          <div className="space-y-2">
            <Label htmlFor="squareFootage">Square Footage (Optional)</Label>
            <Input
              id="squareFootage"
              type="number"
              placeholder="e.g., 5000"
              value={formData.squareFootage}
              onChange={(e) => setFormData((prev) => ({ ...prev, squareFootage: e.target.value }))}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Add any notes or details about this service..."
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
